import React from 'react';
import { Home, ChevronRight, Volume2, Heart, Mountain, Compass, Ship } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface GibbHighlandConnectionSectionProps {
  onSelectPage?: (pageId: string) => void;
}

export const GibbHighlandConnectionSection: React.FC<GibbHighlandConnectionSectionProps> = ({ onSelectPage }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          {/* Breadcrumb: The Gibb Family > Page 1 of 2: Our Highland Connection */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPage && onSelectPage('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Gibb Family</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Page 1 of 2: Our Highland Connection
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to Our Highland Connection"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          OUR HIGHLAND CONNECTION
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Mountain className="w-3.5 h-3.5" />
          <span>Page 1 of the 2-Part Gibb Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section 1: Highland Origins & Lineage */}
        <section className="space-y-6">
          <p>
            The Gibb and Lindsay clans come from the Angus Glens - Glen Clova and Glen Esk.
          </p>

          <p>
            Greta Valley in North Canterbury became home to Helen Gibb nee Lindsay, the practical
            hardworking highland daughter of George Lindsay and Helen nee McAndrew, of Glen Clova,
            perhaps the most beautiful of the glens. George was a shepherd. Helen was a flax weaver. They
            married 23 November 1833 and had six children:
          </p>

          {/* 6 Children List */}
          <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif space-y-2.5 text-base sm:text-lg">
            <p><strong>Jane</strong>, 1834-58, married William Fearn</p>
            <p><strong>Mary</strong>, 1836-1914, married David Glenday</p>
            <p><strong>Helen</strong>, born 1838, was our direct ancestor</p>
            <p><strong>Charles McAndrew</strong>, 1841-1923, had an ironmongery business in Glasgow, and made three visits to New Zealand</p>
            <p><strong>Anne Fortescue</strong>, (1844-1899) who married Irish farm labourer John Hazelton, and come to NZ on the Waimate in 1875 and lived in the Waimate area, working on farms</p>
            <p><strong>George</strong> (1846-1933), was an arts scholar then trained at the Glasgow Free Church College; he came to NZ for the Free Church of Scotland, taking positions at Waihola, Waimate, Otepopo, St Paul's Invercargill, St Clair Dunedin. In 1911 he was Moderator of the General Assembly of the Assembly of Presbyterian Churches in NZ. He married Jessie Fawns Mollison in 1878, and they had 6 children.</p>
          </div>

          {/* Image 136: Glen Clova Parish Church */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/136 Glen Clova Parish Church.jpg"
              alt="Glen Clova Parish Church"
              caption="The Parish Church of Glen Clova (now a community centre)"
              filenameHint="136 Glen Clova Parish Church.jpg"
            />
          </div>

          <p>
            The Parish Church of Glen Clova (now a community centre)
          </p>

          <p>
            Helen McAndrew was born at Glen Clova about 1809. Her father was Charles McAndrew, born
            about 1777 at Angus and her mother was Ann How. They married 11 April 1808. They were both
            born, baptized, married and buried within the parish of Glen Clova. Her grandfather was Alexandeer
            McAndrew, who married Margaret How on 14 July 1774.
          </p>

          <p>
            George Lindsay was born 22 March 1807 at Ruthven, Angus. His father was David Lindsay, born
            about 1872. His mother was Anna Mill for whom there remains very little information.
          </p>

          {/* Image 137: Glen Clova in autumn */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/137 Glen Clova in autumn.jpg"
              alt="Glen Clova in autumn"
              caption="Glen Clova in autumn"
              filenameHint="137 Glen Clova in autumn.jpg"
            />
          </div>

          <p>
            Glen Clova in autumn
          </p>

          <p>
            It is easy to see that the Scots would have been quite familiar with the rolling hill country, gullies and
            streams of North Canterbury. They would have known how to manage sheep on the hills. The
            winters would probably have seemed quite mild to what they were used to, the summers would
            have been hotter.
          </p>

          {/* Image 138: Lindsay Clan crest */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/138 Lindsay Clan crest - Endurance and Strength.jpg"
              alt="Lindsay Clan crest - Endurance and Strength"
              caption="The Lindsay Clan crest and tartan - Endurance strength"
              filenameHint="138 Lindsay Clan crest - Endurance and Strength.jpg"
            />
          </div>

          <p>
            The Lindsay Clan crest and tartan - Endurance strength
          </p>

          <p>
            The Gibb family were from Lochlee, in Glen Esk, the longest and most easterly of the glens, where
            many tenant farmers were evicted during the Highland Clearances and most families saw several of
            their younger ones emigrate.
          </p>

          {/* Image 139 & 140: Lochlee in Glen Esk & Around Glen Esk */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <ArchivalImageCard
              src="/images/139 Lochlee in Glen Esk.jpg"
              alt="Lochlee in Glen Esk"
              caption="Lochlee, Glen Esk"
              filenameHint="139 Lochlee in Glen Esk.jpg"
            />
            <ArchivalImageCard
              src="/images/140 Around Glen Esk.jpg"
              alt="Around Glen Esk"
              caption="The countryside of Glen Esk"
              filenameHint="140 Around Glen Esk.jpg"
            />
          </div>

          <p>
            Lochlee, Glen Esk
          </p>

          <p>
            The countryside of Glen Esk
          </p>

          <p>
            Stewart Burnett Gibb was born in 1836, the son of David Gibb (6 March 1796-8 December 1884)
            and Isobel, nee Jack, born about 1797. Isobel was from St Vigeans - there were 18 farming families
            in 1754 but in 1790 there was just one family left. However, British colonies needed shepherds and
            farm workers and they promised a more prosperous future than most younger people were facing in
            the highlands at that time.
          </p>

          <p>
            David was son of David Gibb and Jean nee Stewart. They had three sons - Stewart, David and
            James. James came to New Zealand after marrying local girl Mary Black in 1863, and also farmed in
            the Motunau area.
          </p>

          <p>
            Isobel Jack's home village houses the St Vigeans Museum of Stones, showcasing 38 monumental
            stones carved by the Picts, who lived in eastern Scotland 400-900 and were Celtic-speaking. They
            were iron age farming people whose carvings told of their daily lives, battles and their saints. They
            defeated the Romans, who called them Picts, because of their tattooed bodies.
          </p>

          <p>
            The name GIBB dates back to the Picts. Pictured - examples of Pict-carved stones.
          </p>

          {/* Images 141 & 142: Pict stones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <ArchivalImageCard
              src="/images/141 Pict stones.jpg"
              alt="Pict stones"
              caption="Pict stones"
              filenameHint="141 Pict stones.jpg"
            />
            <ArchivalImageCard
              src="/images/142 Pict stones attract visitors from around the world.jpg"
              alt="Pict stones attract visitors from around the world"
              caption="Pict stones attract visitors from around the world"
              filenameHint="142 Pict stones attract visitors from around the world.jpg"
            />
          </div>

          <p>
            The Gibbs and Lindsays left behind an area of natural beauty, rich cultural traditions, a long history
            of land occupation by many peoples, distinctive Clans and large extended family networks. They
            would have spoken with a distinctive Scottish burr and accent. Every indication is that they were
            very well liked and respected in their new communities.
          </p>
        </section>

        {/* Section 2: The Ships and Voyage */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e] flex items-center gap-2.5">
            <Ship className="w-6 h-6 text-[#7a5b3a]" />
            <span>The ships that carried Stewart and Helen Gibb from the Angus Glens "around the curve of the world"<sup>1</sup></span>
          </h2>

          <p>
            Imagine a young couple living in beautiful valleys in the shadow of the Grampian Mountains in the
            Scottish Highlands in the 1850's. The Angus Glens are a series of tranquil valleys, but then suffering
            from the Highland Clearances where landlords evicted tenant farmers to have more profitable sheep
            farming themselves on the land. Every family had members who emigrated for better opportunities.
          </p>

          <p>
            Helen was born on Wester Eggie farm, where many of her Lindsay family were living in the glen. She
            had basic schooling, worked in the house and on the farm, and was listed as a dairy maid.
          </p>

          {/* Footnote 1 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p>
              <sup>1</sup> "Around the curve of the world" refers to the dramatic cantata written by Francis Grier with lyrics by Sue
              Mayo, commissioned for the 150th anniversary of the First Four Ships journey from England to Lyttelton in
              1850. It is based on diaries and stories about the journey, portrayed by soloists, Choir and Orchestra.
            </p>
          </div>

          <p>
            She was betrothed to Stewart Lindsay Gibb, and had their first son James Lindsay Gibb in 1858.
          </p>

          <p>
            Two years later Stewart emigrated to NZ, as a shepherd, and he departed Bristol on 2 September
            1860 on the Matoaka, arriving at Lyttelton on 1 December.
          </p>

          {/* Image 143: Matoaka */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/143 Matoaka  that brought Stewart Gibb to NZ in 1860.jpg"
              alt="Matoaka that brought Stewart Gibb to NZ in 1860"
              caption="Matoaka that brought Stewart Gibb to NZ in 1860"
              filenameHint="143 Matoaka  that brought Stewart Gibb to NZ in 1860.jpg"
            />
          </div>

          <p>
            There was plenty of work for shepherds on the sheep farms of North Canterbury and he was quickly
            employed on Teviotdale near Amberley. It was to be three years before the couple were reunited.
          </p>

          <p>
            Helen prepared for the voyage to NZ, leaving from Plymouth on 18 June 1863 on the Accrington. She
            travelled with her infant son, and Stewart's brother James, also classified as a shepherd, aged 24,
            and sister-in-law Mary (19).
          </p>

          <p>
            They arrived 75 days later in Lyttelton on 5 September 1863. It was smooth sailing until they
            rounded the Cape of Good Hope on the southern edge of Africa, when they faced gales and squally
            weather.
          </p>

          <p>
            The Accrington attracted much attention when it berthed at Lyttelton, not merely for the excellent
            comforts it offered but more for the Normandy's large water distilling apparatus that was able to
            supply plenty of fresh water to passengers throughout the journey.
          </p>

          <p>
            The ship was 85m long, weighed 1931 tonnes. By comparison, a Holland America cruise ship is 285m
            long and has 82,500 gross tonnage - about 40 times the size. Captain Cook's Endeavour was 20m
            long and 366 tonnes.
          </p>

          <p>
            Just 12 days after she arrived Helen and Stewart married at Cameron's Hotel, Saltwater Creek north
            of Kaiapoi, with their five-year-old son James Lindsay Gibb happily reunited with his young father.
          </p>

          <p>
            Stewart and Helen worked on Teviotdale Station just north of Amberley and saved to buy 24.5 ha at
            Cabbage Tree Flat. James and Mary bought a farm nearby.
          </p>
        </section>

        {/* Section 3: Visitors from NZ */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e] flex items-center gap-2.5">
            <Compass className="w-6 h-6 text-[#7a5b3a]" />
            <span>VISITORS FROM NZ</span>
          </h2>

          <p>
            When Annette<sup>3</sup> and Derek Benfield visited the area they drove up the other side of the North Esk
            River as far as Tarfside, which was as close as they could get to Loch Lee by road - a footpath carries
            on Loch Lee. There were many deer in the glens, which German tourists hunted for sport. Annette
            said one memorable scene was that of five beautiful pheasants perched on a rustic wooden gate
            with the hills behind. They visited the Glenesk Folk Museum which has a display of domestic and
            agricultural objects to tell the stories of life on the land over the centuries. There is a photo of Mrs
            Robert Gibb on a wall. The last Lindsay in the glen was noted seamstress, Miss Minnie Lindsay.
          </p>

          <p>
            There is a large Maule Memorial Free Church (1852) there, built after the break from the Church of
            England. Christianity had been in the glens since the early 600s after St Drostan set up a church at
            the head of the glen very close to Loch Lee, and became known as the hermit of Glen Esk, who was
            reported to have done several miracles. He was a member of the Royal Family, educated on lona,
            became a disciple of St Colomba and was sent to the Pict lands, where he stayed until his death.
          </p>

          {/* Footnotes 2 and 3 */}
          <div className="space-y-2 p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p>
              <sup>2</sup> French chemist Alphonse Pere Mire de Normandy was a French chemist who got a patent grant in 1851 for
              his evaporator - condensor-cooler system or getting palatable drinking water from seawater, using steam
              from the ship boilers.
            </p>
            <p>
              <sup>3</sup> Annette is a great great granddaughter of Helen and Stewart Gibb through Annie Gibb and her daughter
              Margaret.
            </p>
          </div>

          {/* Image 144: Glen Clova Hotel */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/144 Glen Clova Hotel was originally a drovers_ inn. Photo by Annette Benfield.jpg"
              alt="Glen Clova Hotel was originally a drovers_ inn. Photo by Annette Benfield"
              caption="Glen Clova Hotel was originally a Drover's Inn and was well established when Helen and Stewart Gibb were living in the glens. Annette and Derek stayed here. Annette recalled she had to boil the water as it was brown. (Photo: Annette Benfield)"
              filenameHint="144 Glen Clova Hotel was originally a drovers_ inn. Photo by Annette Benfield.jpg"
            />
          </div>

          <p>
            Glen Clova Hotel was originally a Drover's Inn and was well established when Helen and Stewart Gibb were
            living in the glens. Annette and Derek stayed here. Annette recalled she had to boil the water as it was brown.
            (Photo: Annette Benfield)
          </p>
        </section>

        {/* Navigation / Next Page CTA */}
        <div className="pt-8 border-t-2 border-[#d8ceb9] flex flex-wrap items-center justify-between gap-4">
          <button
            onClick={() => onSelectPage && onSelectPage('hub')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#7a5b3a] font-serif font-bold text-sm transition cursor-pointer"
          >
            <Home className="w-4 h-4" />
            <span>Gibb Family Overview</span>
          </button>

          <button
            onClick={() => onSelectPage && onSelectPage('page2')}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-sm transition shadow-sm cursor-pointer"
          >
            <span>Proceed to Page 2</span>
            <ChevronRight className="w-4 h-4 text-amber-200" />
          </button>
        </div>

      </article>
    </div>
  );
};
